import type { PointsBalance } from "./pointsTypes";

/**
 * 點數換算率：1 點 = 1 元。
 * 存氣點數本來就是「氣」折算成的點、帳戶點數本來就是留在瓦斯行的錢，兩者都以 1:1 記帳。
 * 若日後瓦斯行要用別的換算（例：回饋點 10 點折 1 元），只需改這裡與後端的一致定義。
 */
export const POINT_TO_NTD = 1;

export const pointsToNtd = (points: number): number => Math.floor(Math.max(0, points) * POINT_TO_NTD);
export const ntdToPoints = (ntd: number): number => Math.floor(Math.max(0, ntd) / POINT_TO_NTD);

export interface PointsRedeemInput {
  /** 券後的瓦斯品項小計（存氣點數的折抵天花板 —— 存氣只能折氣，不折商品/樓層費/服務費） */
  gasSubtotalAfterCoupon: number;
  /** 券後應付總額（含費用、樓層費）；點數在券之後才折 */
  payableAfterCoupon: number;
  balance: PointsBalance;
  /** 使用者想折的存氣點數 */
  wantStoredGas: number;
  /** 使用者想折的帳戶點數 */
  wantAccount: number;
}

export interface PointsRedeemResult {
  /** 實際折抵的存氣點數（點） */
  storedGas: number;
  /** 實際折抵的帳戶點數（點） */
  account: number;
  /** 兩種點數折抵的金額合計（元） */
  discount: number;
  /** 折後應付金額（元），不會低於 0 */
  payable: number;
  /** 目前可折的存氣點數上限 */
  maxStoredGas: number;
  /** 扣掉已折的存氣點數後，可折的帳戶點數上限 */
  maxAccount: number;
}

const clampInt = (n: number, max: number): number => {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(Math.floor(n), Math.max(0, Math.floor(max)));
};

/**
 * 折抵順序：優惠券（後端 preview 已算）→ 存氣點數 → 帳戶點數。
 *
 * 存氣點數先折，因為它的用途最窄（只能折瓦斯）：把窄的先用掉，
 * 寬的（帳戶點數，可折全單）才不會被卡住變成折不完。
 */
export const calcPointsRedeem = ({
  gasSubtotalAfterCoupon,
  payableAfterCoupon,
  balance,
  wantStoredGas,
  wantAccount,
}: PointsRedeemInput): PointsRedeemResult => {
  const payable = Math.max(0, Math.floor(payableAfterCoupon));
  const gasCap = Math.max(0, Math.min(Math.floor(gasSubtotalAfterCoupon), payable));

  const maxStoredGas = ntdToPoints(Math.min(pointsToNtd(balance.storedGas), gasCap));
  const storedGas = clampInt(wantStoredGas, maxStoredGas);
  const storedGasNtd = pointsToNtd(storedGas);

  const remaining = Math.max(0, payable - storedGasNtd);
  const maxAccount = ntdToPoints(Math.min(pointsToNtd(balance.account), remaining));
  const account = clampInt(wantAccount, maxAccount);
  const accountNtd = pointsToNtd(account);

  const discount = storedGasNtd + accountNtd;
  return {
    storedGas,
    account,
    discount,
    payable: Math.max(0, payable - discount),
    maxStoredGas,
    maxAccount,
  };
};
